import { API_ENDPOINT } from "@/constants/appConstants";
import { useLogin } from "@/context/useLoginProps";
import { useNetwork } from "@/hooks/useNetwork";
import useSocialLogin from "@/hooks/useSocialLogin";
import { getQueryParams } from "@/utils/util";
import { Button } from "@monsterindia/global-ui-components";
import React, { useEffect } from "react";

import { SocialButtonProps } from "@/components/login/types";

type ValidateResponse = {
  data: {
    validationStatus?: string;
    accessToken?: string;
    message?: string;
  };
  error?: string;
  isloading?: boolean;
};

const SocialButton: React.FC<SocialButtonProps> = ({ icon, label }) => {
  const { fetchData: fetchAPI } = useNetwork();
  const { socialUser, handleSocialLogin, isLoading } = useSocialLogin();
  const { hostname, handleEventTracking } = useLogin();

  const doAutoLogin = async (data: any) => {
    const clientId =
      getQueryParams()["client_id"] ?? "fd111af2-493d-11e8-9621-24a074f06450";

    let reqData = {
      username: data.socialAccessToken,
      social_provider: data.socialProvider,
      grant_type: "password",
      scope: "all",
      client_id: clientId,
    };
    try {
      const resp = await fetchAPI(
        API_ENDPOINT(hostname).middleware.doAutoLogin,
        "POST",
        {},
        reqData,
      );
      const returnURL = getQueryParams()["return_url"];
      if (resp && resp.data) {
        window.location.href = returnURL ?? "/seeker/dashboard";
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (socialUser && !socialUser.error) {
        let reqData = {
          socialProvider: socialUser.provider,
          socialAccessToken: socialUser.accessToken,
        };
        try {
          const resp = (await fetchAPI(
            API_ENDPOINT(hostname).middleware.validateSocial,
            "POST",
            {},
            reqData,
          )) as ValidateResponse;
          if (resp && resp.data && resp.data.validationStatus) {
            const { validationStatus } = resp.data;
            if (validationStatus === "USER_EXISTS") {
              // auto login
              await doAutoLogin(reqData);
              handleEventTracking({
                eventType: "eventPush",
                eventName: "Login_Social_Success",
                eventProps: {
                  provider: socialUser.provider || "",
                },
              });
              if (socialUser?.email) {
                handleEventTracking({
                  eventType: "onUserLoginPush",
                  eventProps: {
                    email: socialUser.email,
                  },
                });
              }
              handleEventTracking({
                eventType: "eventPush",
                eventName: "Login_Completed",
                eventProps: {
                  method: "social",
                },
              });
            } else {
              if (socialUser) {
                // handleAfterSocialLogin(socialUser);
                window.location.href =
                  "/seeker/registration?provider=" +
                  socialUser.provider +
                  "&accessToken=" +
                  socialUser.accessToken +
                  "&email=" +
                  (socialUser.email ?? "");
              }
            }
          }
        } catch (error) {
          console.error("Error fetching data:", error);
          handleEventTracking({
            eventType: "eventPush",
            eventName: "Login_Social_Error",
            eventProps: {
              error_type: (error as Error)?.message || "failed social login",
            },
          });
        }
      }
    };
    fetchData();
  }, [socialUser]);

  const handleSocialButtonClick = (social: string) => {
    switch (social.toLowerCase()) {
      case "google":
        handleSocialLogin("google");
        handleEventTracking({
          eventType: "eventPush",
          eventName: "Login_Social_Clicked",
          eventProps: {
            provider: "google",
          },
        });
        break;
      case "linkedin":
        handleSocialLogin("linkedin");
        handleEventTracking({
          eventType: "eventPush",
          eventName: "Login_Social_Clicked",
          eventProps: {
            provider: "linkedin",
          },
        });
        break;
      default:
        handleSocialLogin("google");
        break;
    }
  };

  const loading = label === "Google" ? false : isLoading.linkedin;

  return (
    <Button
      label={label}
      modifier="primary-white"
      type="button"
      onClick={() => handleSocialButtonClick(label)}
      isLoading={loading}
      disabled={loading}
      className="flex min-h-11 flex-1 shrink basis-0 items-center justify-center gap-2 rounded-[52px] border border-solid border-[color:var(--Border-Subtle,#E8E8EB)] px-2 py-2.5 text-center text-sm font-medium text-gray-800"
      leftIcon={
        !loading && (
          <img
            src={icon}
            alt={`${label} icon`}
            className="my-auto aspect-square w-6 shrink-0 self-stretch overflow-hidden object-contain"
          />
        )
      }
    ></Button>
  );
};

export default SocialButton;
